import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, MapPin,
  Wind, Droplets, Sunrise, Sunset, Thermometer, Search, X, RefreshCw, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  clear: Sun,
  "partly-cloudy": Cloud,
  cloudy: Cloud,
  fog: CloudFog,
  rain: CloudRain,
  snow: CloudSnow,
  thunderstorm: CloudLightning,
};

function WeatherIcon({ icon, size = 20, className = "" }) {
  const I = ICONS[icon] || Cloud;
  return <I size={size} strokeWidth={1.5} className={className} />;
}

function fmtTemp(v) {
  return v == null ? "—" : `${Math.round(v)}°`;
}
function fmtWind(v) {
  return v == null ? "—" : `${Math.round(v)} mph`;
}
function fmtPct(v) {
  return v == null ? "—" : `${Math.round(v)}%`;
}
function fmtLocalTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (e) { return iso; }
}

/**
 * <WeatherWidget variant="forecast|now" eventId canEdit />
 *
 * - "forecast": planning card showing the daily forecast for the event date.
 * - "now":      compact live-tab card showing current conditions + a 24h strip.
 *
 * When no weather_location is set, both variants render an empty state with a
 * "Set location" button (planners only). Fetches auto-refresh on mount.
 */
export default function WeatherWidget({ eventId, variant = "forecast", canEdit = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const path = variant === "now" ? "now" : "forecast";
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/weather/${path}`);
      setData(data);
    } catch (e) {
      console.error(`weather ${path} load failed:`, e);
      setData({ status: "error" });
    } finally { setLoading(false); setRefreshing(false); }
  }, [eventId, path]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => { setRefreshing(true); await load(); };

  const clearLocation = async () => {
    if (!confirm("Remove the weather location for this event?")) return;
    try {
      await api.delete(`/events/${eventId}/weather-location`);
      setData({ status: "no_location" });
      toast.success("Weather location cleared");
    } catch (e) { toast.error("Could not clear"); }
  };

  const onLocationSaved = () => {
    setLoading(true);
    load();
  };

  const status = data?.status || (loading ? "loading" : "unknown");
  const loc = data?.location;

  const wrapper = (children, actions = null) => (
    <div
      data-testid={`weather-${variant}-card`}
      className={`bevel border-hair rounded-sm ${variant === "now" ? "p-4" : "p-5"}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <WeatherIcon icon={data?.current?.weather?.icon || data?.forecast?.weather?.icon || "cloudy"} size={13} className="text-app" />
        <div className="eyebrow">
          {variant === "now" ? "Current conditions" : "Forecast"}
        </div>
        {loc && (
          <div className="ml-2 text-[11px] text-muted truncate flex items-center gap-1">
            <MapPin size={9} strokeWidth={1.5} /> {loc.label}
          </div>
        )}
        {actions ? <div className="ml-auto flex items-center gap-1">{actions}</div> : (
          canEdit && loc && (
            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={refresh}
                disabled={refreshing}
                data-testid={`weather-${variant}-refresh`}
                title="Refresh"
                className="text-muted hover:text-app p-1 disabled:opacity-50"
              >
                <RefreshCw size={11} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
              </button>
              <button
                onClick={() => setDialogOpen(true)}
                data-testid={`weather-${variant}-change`}
                title="Change location"
                className="text-muted hover:text-app p-1"
              >
                <Search size={11} strokeWidth={1.5} />
              </button>
              <button
                onClick={clearLocation}
                data-testid={`weather-${variant}-clear`}
                title="Remove location"
                className="text-muted hover:text-red-600 p-1"
              >
                <Trash2 size={11} strokeWidth={1.5} />
              </button>
            </div>
          )
        )}
      </div>
      {children}
    </div>
  );

  if (loading) return wrapper(<div className="text-xs text-muted">Loading…</div>);

  if (status === "no_location") {
    return (
      <>
        {wrapper(
          <div>
            <p className="text-sm text-muted font-light">
              {variant === "now"
                ? "Set the event location to see current conditions on the day."
                : "Set the event location to see the forecast for your date."}
            </p>
            {canEdit && (
              <Button
                onClick={() => setDialogOpen(true)}
                variant="outline"
                size="sm"
                data-testid={`weather-${variant}-set-location`}
                className="rounded-sm text-xs mt-3"
              >
                <MapPin size={11} strokeWidth={1.5} className="mr-1.5" /> Set weather location
              </Button>
            )}
          </div>
        )}
        <LocationDialog
          eventId={eventId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSaved={onLocationSaved}
        />
      </>
    );
  }

  if (status === "too_far_out") {
    return wrapper(
      <div className="flex items-start gap-3">
        <WeatherIcon icon="cloudy" size={24} className="text-muted" />
        <div>
          <div className="font-serif text-lg text-app">Forecast opens in {(data.days_out || 0) - 16} more days</div>
          <p className="text-xs text-muted font-light mt-1">
            Open-Meteo can only see 16 days ahead. Check back closer to the date.
          </p>
        </div>
      </div>
    );
  }
  if (status === "in_past") {
    return wrapper(<div className="text-sm text-muted font-light">Event has already happened.</div>);
  }
  if (status === "no_date") {
    return wrapper(<div className="text-sm text-muted font-light">Set the event date to see a forecast.</div>);
  }
  if (status === "error") {
    return wrapper(
      <div>
        <div className="text-sm text-muted font-light">Couldn&apos;t reach the weather provider.</div>
        <Button onClick={refresh} variant="outline" size="sm" className="rounded-sm text-xs mt-2">Retry</Button>
      </div>
    );
  }

  // status === "ok"
  if (variant === "forecast") {
    const f = data.forecast || {};
    return (
      <>
        {wrapper(
          <div>
            <div className="flex items-start gap-4">
              <WeatherIcon icon={f.weather?.icon} size={44} className="text-app" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <div className="font-serif text-3xl text-app leading-none" data-testid="weather-forecast-high">
                    {fmtTemp(f.temp_high)}
                  </div>
                  <div className="text-lg text-muted leading-none" data-testid="weather-forecast-low">
                    / {fmtTemp(f.temp_low)}
                  </div>
                </div>
                <div className="text-sm text-app mt-1">{f.weather?.label}</div>
                <div className="text-[11px] text-muted mt-0.5">
                  {data.days_out === 0 ? "Today" : data.days_out === 1 ? "Tomorrow" : `In ${data.days_out} days`}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-xs">
              <Row icon={<Droplets size={11} strokeWidth={1.5} />} label="Rain chance" value={fmtPct(f.precip_prob)} testid="wf-precip-prob" />
              <Row icon={<Droplets size={11} strokeWidth={1.5} />} label="Precip" value={f.precip_amount == null ? "—" : `${f.precip_amount.toFixed(2)} in`} />
              <Row icon={<Wind size={11} strokeWidth={1.5} />} label="Wind max" value={fmtWind(f.wind_max)} testid="wf-wind" />
              <Row icon={<Thermometer size={11} strokeWidth={1.5} />} label="Feels" value={f.temp_high != null ? fmtTemp(f.temp_high) : "—"} />
              <Row icon={<Sunrise size={11} strokeWidth={1.5} />} label="Sunrise" value={fmtLocalTime(f.sunrise)} />
              <Row icon={<Sunset size={11} strokeWidth={1.5} />} label="Sunset" value={fmtLocalTime(f.sunset)} />
            </div>
          </div>
        )}
        <LocationDialog
          eventId={eventId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSaved={onLocationSaved}
          initialLabel={loc?.label}
        />
      </>
    );
  }

  // variant === "now"
  const c = data.current || {};
  return (
    <>
      {wrapper(
        <div>
          <div className="flex items-start gap-3">
            <WeatherIcon icon={c.weather?.icon} size={36} className="text-app" />
            <div className="flex-1">
              <div className="font-serif text-2xl text-app leading-none" data-testid="weather-now-temp">
                {fmtTemp(c.temp)}
              </div>
              <div className="text-xs text-muted mt-1">
                {c.weather?.label} · feels {fmtTemp(c.feels_like)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-[11px] text-muted">
            <div className="flex items-center gap-1"><Wind size={10} strokeWidth={1.5} /> {fmtWind(c.wind)}</div>
            <div className="flex items-center gap-1"><Droplets size={10} strokeWidth={1.5} /> {fmtPct(c.humidity)}</div>
            <div className="flex items-center gap-1"><Droplets size={10} strokeWidth={1.5} /> {c.precip == null ? "—" : `${c.precip.toFixed(2)} in`}</div>
          </div>
          {Array.isArray(data.hourly) && data.hourly.length > 0 && (
            <div className="mt-3 pt-3 border-t border-hair" data-testid="weather-now-hourly">
              <div className="eyebrow mb-1.5">Next 24 hours</div>
              <div className="flex gap-1 overflow-x-auto pb-1">
                {data.hourly.map((h, i) => (
                  <div key={h.time || `h-${i}`} className="flex flex-col items-center min-w-[42px] text-[10px] text-muted">
                    <div>{h.time ? new Date(h.time).getHours() + "h" : ""}</div>
                    <WeatherIcon icon={h.weather?.icon} size={13} className="text-app my-0.5" />
                    <div className="text-app">{fmtTemp(h.temp)}</div>
                    <div className="text-muted">{h.precip_prob != null ? `${Math.round(h.precip_prob)}%` : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <LocationDialog
        eventId={eventId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={onLocationSaved}
        initialLabel={loc?.label}
      />
    </>
  );
}

function Row({ icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-muted flex items-center gap-1.5">{icon} {label}</span>
      <span className="text-app font-medium">{value}</span>
    </div>
  );
}

function LocationDialog({ eventId, open, onOpenChange, onSaved, initialLabel = "" }) {
  const [q, setQ] = useState(initialLabel || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setResults([]); setQ(initialLabel || ""); }
  }, [open, initialLabel]);

  const search = async () => {
    const term = q.trim();
    if (!term) { toast.error("Type a place first"); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/geo/search?q=${encodeURIComponent(term)}`);
      setResults(data.results || []);
      if ((data.results || []).length === 0) {
        toast.info("No matches — try a nearby city or just the town name.");
      }
    } catch (e) {
      console.error("geo search failed:", e);
      toast.error("Search failed");
    } finally { setSearching(false); }
  };

  const pick = async (r) => {
    setSaving(true);
    try {
      await api.patch(`/events/${eventId}/weather-location`, {
        label: r.label,
        lat: r.lat,
        lng: r.lng,
        timezone: r.timezone,
      });
      toast.success(`Weather set to ${r.label}`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Could not save location");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-app">Set weather location</DialogTitle>
          <DialogDescription>
            Type the venue city or region. We&apos;ll use it to pull the forecast for your date and live conditions on event day.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input
            data-testid="weather-loc-input"
            placeholder="e.g. Napa Valley, CA · Marfa, TX · Lisbon, Portugal"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            autoFocus
          />
          <Button
            onClick={search}
            disabled={searching}
            data-testid="weather-loc-search"
            className="rounded-sm bg-app hover:bg-[#000000]"
          >
            {searching ? "…" : <Search size={14} strokeWidth={1.5} />}
          </Button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 mt-2" data-testid="weather-loc-results">
          {results.length === 0 && !searching && (
            <p className="text-xs text-muted font-light py-2">Search to see matches.</p>
          )}
          {results.map((r) => (
            <button
              key={`${r.lat}-${r.lng}`}
              onClick={() => pick(r)}
              disabled={saving}
              data-testid={`weather-loc-pick-${r.lat}-${r.lng}`}
              className="w-full text-left px-3 py-2 border border-hair rounded-sm hover:border-[#0A0A0A] hover:bg-canvas transition-colors flex items-center gap-2"
            >
              <MapPin size={13} strokeWidth={1.5} className="text-app flex-none" />
              <div className="min-w-0">
                <div className="text-sm text-app truncate">{r.label}</div>
                <div className="text-[10px] text-muted">
                  {r.lat.toFixed(3)}, {r.lng.toFixed(3)} · {r.timezone}
                </div>
              </div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            <X size={14} strokeWidth={1.5} className="mr-1.5" /> Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
